import * as Sentry from '@sentry/browser'
import type { Ref } from 'vue'
import { MS_PER } from '@/services/types/Time'

const waitForClerkToLoad = async (isLoaded: Ref<boolean>) => {
  try {
    if (isLoaded.value) {
      return true
    }
    await new Promise<void>((resolve) => {
      let settled = false
      let stopWatcher: (() => void) | null = null
      let timeoutId: number | null = null

      const cleanup = () => {
        if (settled) { return }
        settled = true
        if (stopWatcher) {
          stopWatcher()
        }
        if (timeoutId !== null) {
          window.clearTimeout(timeoutId)
        }
        resolve()
      }
      stopWatcher = watch(isLoaded, (val) => {
        if (val) {
          cleanup()
        }
      })
      timeoutId = window.setTimeout(cleanup, MS_PER.Second * 10)
    })
    return isLoaded.value
  } catch {
    return false
  }
}

export default () => {
  const snackbar = useSnackbar()
  const app = useNuxtApp()

  const getEffectiveAuthToken = async (): Promise<string | null> => {
    try {
      const CLERK_SSO_AUTH_ENABLED = import.meta.env.VITE_CLERK_SSO_AUTH_ENABLED === 'true'
      if (CLERK_SSO_AUTH_ENABLED) {
        const auth = app.vueApp.runWithContext(() => useAuth())
        await waitForClerkToLoad(auth.isLoaded)
        const clerkToken = await auth.getToken.value?.() || null
        if (clerkToken) {
          return `Bearer ${clerkToken}`
        }
      }
      const legacyToken = localStorage.getItem('auth-token')
      if (legacyToken) {
        return `JWT ${legacyToken}`
      }
      return null
    } catch (error) {
      Sentry.captureException(error)
      snackbar.error('Authentication token unavailable. Please refresh and try again.')
      return null
    }
  }

  const getEffectiveAuthTokenRaw = async (): Promise<string | null> => {
    const token = await getEffectiveAuthToken()
    if (token) {
      return token.split(' ')[1]
    }
    return null
  }

  return {
    getEffectiveAuthToken,
    getEffectiveAuthTokenRaw
  }
}
